import React, { type PropsWithChildren, useState } from 'react';

import IconButton from '@atlaskit/button/icon/button';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import type { BackgroundColor } from '@atlaskit/primitives/compiled/utils/types';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Text } from '@atlaskit/primitives/compiled/text';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';

import { token } from '@atlaskit/tokens';
import { cssMap } from '@atlaskit/css';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';

const toggleButtonLabel = defineMessage({
	id: 'polaris-lib-expandable-section.toggle-button-label',
	defaultMessage: 'Toggle',
	description: 'Label for button to expand/collapse the section',
});

const styles = cssMap({
	container: {
		gap: token('space.100'),
		display: 'flex',
		flexDirection: 'column',
		borderRadius: token('radius.small'),
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
	header: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		cursor: 'pointer',
		gap: token('space.050'),
		backgroundColor: 'transparent',
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
	},
});

export type ExpandableSectionProps = PropsWithChildren<{
	title: string;
	onToggle?: (isExpanded: boolean) => void;
	backgroundColor?: BackgroundColor;
	testId?: string;
	initialIsExpanded?: boolean;
}>;

export const ExpandableSection: (props: ExpandableSectionProps) => React.JSX.Element = ({
	title,
	onToggle,
	children,
	backgroundColor = 'color.background.accent.yellow.subtlest',
	testId = 'polaris-lib-expandable-section.ui',
	initialIsExpanded = false,
}: ExpandableSectionProps): React.JSX.Element => {
	const [isExpanded, setIsExpanded] = useState(initialIsExpanded);

	const ToggleIcon = isExpanded ? ChevronUpIcon : ChevronDownIcon;

	const { formatMessage } = useIntl();

	const handleToggleClick = () => {
		setIsExpanded((prevIsExpanded) => {
			onToggle?.(!prevIsExpanded);
			return !prevIsExpanded;
		});
	};

	return (
		<Box testId={testId} xcss={styles.container} backgroundColor={backgroundColor}>
			<Pressable xcss={styles.header} onClick={handleToggleClick}>
				<Text>{title}</Text>
				{fg('jpd_roadmap_publish_fe') ? (
					<ToggleIcon
						testId={`${testId}.toggle-button`}
						label={formatMessage(toggleButtonLabel)}
						color={token('color.icon')}
						size="small"
					/>
				) : (
					<IconButton
						testId={`${testId}.toggle-button`}
						appearance="subtle"
						label={formatMessage(toggleButtonLabel)}
						icon={(iconProps) => (
							<ToggleIcon {...iconProps} color={token('color.icon')} size="small" />
						)}
					/>
				)}
			</Pressable>
			{isExpanded && <>{children}</>}
		</Box>
	);
};
